import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
// One import for every shared form essential (BO UI Standard §18.6) — this page
// is the proof that a form wires its title/notify/save from the shared kit.
import {
  FormTitle, LoaderSymbol, AppToolbar, AppDataGrid, type AppColumn, useFormShell,
} from '../../formKit';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { navTree, canonMod, type NavGroupChild } from '../../layouts/navData';

// Normalise a form caption / nav label to match across the DB ↔ navData boundary
// (lower-case, strip spaces & punctuation). NOT canonMod — that applies MODULE
// aliases that would wrongly rewrite a form label.
const canonLabel = (s: string) => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
import './security.css';

// ─────────────────────────────────────────────────────────────────────────
// User & Security → Navigation Manager (two tabs: Modules · Forms)
// ─────────────────────────────────────────────────────────────────────────
//   Modules / forms come from dbo.NavigationItem (+ dbo.DashboardData).  The
//   default SORT follows the live sidebar order (navData) — modules are ordered
//   by their sidebar group position; forms by their module's order then their
//   own sort.  The Sort column pre-fills that baseline (1-based); only an
//   explicitly-edited number persists.  Modules + forms are both NavigationItem
//   rows (caption → NavigationCustomCaption, sort → AISortOrder).
// ─────────────────────────────────────────────────────────────────────────

function lcKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(lcKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k.toLowerCase()] = lcKeys(val);
    return out;
  }
  return v;
}
const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
  ?? (e as { message?: string }).message ?? 'Request failed.';

type FormRow = { oid: string; caption: string | null; customCaption: string | null; parent: string | null; sortOrder: number | null; source: string };
type ModRow = { oid: string; name: string; customCaption: string | null; sortOrder: number | null };
type FormRowB = FormRow & { baseline: number };
type ModRowB = ModRow & { baseline: number };

type Rec = Record<string, unknown>;
const arr = (data: unknown): Rec[] => (((lcKeys(data) as { data?: Rec[] }).data) ?? []);
const mapForm = (r: Rec): FormRow => ({
  oid: String(r.oid), caption: (r.caption as string) ?? null, customCaption: (r.customcaption as string) ?? null,
  parent: (r.parent as string) ?? null, sortOrder: r.sortorder == null ? null : Number(r.sortorder), source: String(r.source ?? 'nav'),
});
const mapMod = (r: Rec): ModRow => ({
  oid: String(r.oid), name: String(r.name ?? ''), customCaption: (r.customcaption as string) ?? null, sortOrder: r.sortorder == null ? null : Number(r.sortorder),
});

export default function NavigationManagerPage() {
  // useFormShell(pageId) returns the pre-wired notify + save flow + the nav-
  // sourced <FormTitle> props (title/icon/colour), so the page never re-derives
  // them by hand.
  const { notify, runSave, titleProps } = useFormShell('sec-navmgr');

  const [forms, setForms] = useState<FormRow[]>([]);
  const [mods, setMods] = useState<ModRow[]>([]);
  const [tab, setTab] = useState<'modules' | 'forms'>('modules');
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const [capEdits, setCapEdits] = useState<Record<string, string>>({});
  const [sortEdits, setSortEdits] = useState<Record<string, string>>({});

  const load = () => {
    setLoading(true); setErr(null);
    const t0 = performance.now();
    Promise.all([api.get('/api/v1/navigation/items'), api.get('/api/v1/navigation/modules')])
      .then(([fi, mi]) => { setForms(arr(fi.data).map(mapForm)); setMods(arr(mi.data).map(mapMod)); setCapEdits({}); setSortEdits({}); })
      .catch(e => setErr(errMsg(e)))
      .finally(() => { setLoadMs(performance.now() - t0); setLoading(false); });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sidebar order (navData) → module rank.  Sections + groups in tree order. ──
  const navOrder = useMemo(() => {
    const m = new Map<string, number>(); let i = 0;
    for (const e of navTree) {
      if (e.kind === 'section') m.set(canonMod(e.label), i++);
      else if (e.kind === 'group') m.set(canonMod(e.group.label), i++);
    }
    return m;
  }, []);

  // Sidebar order (navData) → per-FORM rank. Walk every leaf item (groups' flat
  // items + items nested in sub-groups, in tree order) so a form's default order
  // in the Navigation Manager matches how it actually appears in the sidebar —
  // NOT alphabetical. Keyed by canonLabel(label) since form rows carry only a
  // caption, no navData id.
  const navFormOrder = useMemo(() => {
    const m = new Map<string, number>(); let i = 0;
    const add = (label?: string) => { if (label) { const k = canonLabel(label); if (!m.has(k)) m.set(k, i++); } };
    const walk = (children?: NavGroupChild[]) => {
      for (const c of children ?? []) {
        if (c.kind === 'item') add(c.item.label);
        else if (c.kind === 'subgroup') walk(c.subgroup.children);
      }
    };
    for (const e of navTree) {
      if (e.kind === 'item') add(e.item.label);
      else if (e.kind === 'group') { for (const it of e.group.items) add(it.label); walk(e.group.children); }
    }
    return m;
  }, []);

  // Modules ordered by stored sort → sidebar position → name; baseline = 1-based position.
  const orderedMods: ModRowB[] = useMemo(() => {
    return [...mods]
      .map(m => ({ m, k: m.sortOrder ?? (navOrder.get(canonMod(m.name)) ?? 9999) }))
      .sort((a, b) => a.k - b.k || a.m.name.localeCompare(b.m.name))
      .map((x, i) => ({ ...x.m, baseline: i + 1 }));
  }, [mods, navOrder]);

  const moduleRank = useMemo(() => {
    const r = new Map<string, number>();
    orderedMods.forEach((m, i) => r.set(canonMod(m.name), i));
    return r;
  }, [orderedMods]);

  const moduleOf = (f: FormRow) =>
    f.source === 'dashboard' ? canonMod('Dashboards & Backlog') : canonMod((f.parent ?? '').split('\\')[0] ?? '');

  // Forms ordered by module rank → (stored sort, else sidebar position) → name;
  // baseline = per-module 1-based position. The secondary key uses the navData
  // leaf order so the DEFAULT matches the sidebar; a saved AISortOrder still wins.
  const orderedForms: FormRowB[] = useMemo(() => {
    const navKey = (f: FormRow) => f.sortOrder ?? navFormOrder.get(canonLabel(f.caption ?? '')) ?? 99999;
    const sorted = [...forms]
      .map(f => ({ f, mr: moduleRank.get(moduleOf(f)) ?? 9999 }))
      .sort((a, b) => a.mr - b.mr || navKey(a.f) - navKey(b.f) || (a.f.caption ?? '').localeCompare(b.f.caption ?? ''));
    const counter = new Map<string, number>();
    return sorted.map(x => { const mod = moduleOf(x.f); const c = (counter.get(mod) ?? 0) + 1; counter.set(mod, c); return { ...x.f, baseline: c }; });
  }, [forms, moduleRank, navFormOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── edit helpers (sort uses stored ?? baseline as the "current" value) ──
  const capVal = (key: string, orig: string | null) => capEdits[key] ?? orig ?? '';
  const capChanged = (key: string, orig: string | null) => key in capEdits && (capEdits[key] ?? '').trim() !== (orig ?? '').trim();
  const sortDisp = (key: string, stored: number | null, baseline: number) => sortEdits[key] ?? String(stored ?? baseline);
  const sortChanged = (key: string, stored: number | null, baseline: number) => key in sortEdits && (sortEdits[key] ?? '').trim() !== String(stored ?? baseline);
  const setCap = (key: string, v: string) => setCapEdits(e => ({ ...e, [key]: v }));
  const setSort = (key: string, v: string) => setSortEdits(e => ({ ...e, [key]: v.replace(/[^0-9]/g, '') }));

  const dirtyForms = useMemo(() => orderedForms.filter(f => capChanged(f.oid, f.customCaption) || sortChanged(f.oid, f.sortOrder, f.baseline)), [orderedForms, capEdits, sortEdits]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirtyMods = useMemo(() => orderedMods.filter(m => capChanged(m.oid, m.customCaption) || sortChanged(m.oid, m.sortOrder, m.baseline)), [orderedMods, capEdits, sortEdits]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirtyCount = dirtyForms.length + dirtyMods.length;

  const matchesSearch = (...parts: (string | null)[]) => {
    const t = search.trim().toLowerCase();
    return !t || parts.some(p => (p ?? '').toLowerCase().includes(t));
  };
  const shownForms = useMemo(() => orderedForms.filter(f => matchesSearch(f.caption, f.customCaption, f.parent)), [orderedForms, search]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownMods = useMemo(() => orderedMods.filter(m => matchesSearch(m.name, m.customCaption)), [orderedMods, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => {
    if (dirtyCount === 0) { notify.info('No changes to save.'); return; }
    setSaving(true);
    runSave({
      formName: 'Navigation',
      auditAction: 'edit',
      save: async () => {
        for (const f of dirtyForms) {
          const s = sortDisp(f.oid, f.sortOrder, f.baseline).trim();
          await api.put(`/api/v1/navigation/items/${f.oid}/caption`, {
            caption: capVal(f.oid, f.customCaption).trim(), sortOrder: s === '' ? null : parseInt(s, 10), hasSort: f.source !== 'dashboard',
          });
        }
        for (const m of dirtyMods) {
          const s = sortDisp(m.oid, m.sortOrder, m.baseline).trim();
          await api.put(`/api/v1/navigation/items/${m.oid}/caption`, {
            caption: capVal(m.oid, m.customCaption).trim(), sortOrder: s === '' ? null : parseInt(s, 10), hasSort: true,
          });
        }
        load();
        // Tell AppShell to re-fetch the nav-config feed → the sidebar relabels/reorders
        // immediately, without a page reload.
        window.dispatchEvent(new Event('ai-nav-config-changed'));
      },
      successMessage: `Updated ${dirtyCount} item(s).`,
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSaving(false));
  };

  const captionCell = (key: string, orig: string | null) => (
    <input className="sec-input" value={capVal(key, orig)} onChange={e => setCap(key, e.target.value)} placeholder="(use default)" maxLength={250} />
  );
  const sortCell = (key: string, stored: number | null, baseline: number, disabled = false) => (
    <input className="sec-input" style={{ width: 70, textAlign: 'right' }} value={sortDisp(key, stored, baseline)} onChange={e => setSort(key, e.target.value)}
      placeholder="—" inputMode="numeric" disabled={disabled} title={disabled ? 'Dashboards have no sort order' : 'Display order'} />
  );

  const modColumns: AppColumn<ModRowB>[] = [
    { field: 'name', header: 'Module name', minWidth: 220, value: r => r.name },
    { field: 'custom', header: 'Custom caption', minWidth: 240, value: r => capVal(r.oid, r.customCaption) || '(use default)', render: r => captionCell(r.oid, r.customCaption) },
    { field: 'sort', header: 'Sort', align: 'right', width: 90, value: r => { const s = sortDisp(r.oid, r.sortOrder, r.baseline); return s === '' ? 0 : Number(s); }, render: r => sortCell(r.oid, r.sortOrder, r.baseline) },
  ];
  const formColumns: AppColumn<FormRowB>[] = [
    // Module is the FIRST column (grouping key); every column is filterable —
    // the editable cells expose a `value` accessor so the Excel-style filter +
    // sort have real data to work on.
    { field: 'parent', header: 'Module', minWidth: 150, value: r => r.parent ?? '—' },
    { field: 'caption', header: 'Form name', minWidth: 220, value: r => r.caption ?? '—' },
    { field: 'custom', header: 'Custom caption', minWidth: 240, value: r => capVal(r.oid, r.customCaption) || '(use default)', render: r => captionCell(r.oid, r.customCaption) },
    { field: 'sort', header: 'Sort', align: 'right', width: 90, value: r => { const s = sortDisp(r.oid, r.sortOrder, r.baseline); return s === '' ? 0 : Number(s); }, render: r => sortCell(r.oid, r.sortOrder, r.baseline, r.source === 'dashboard') },
  ];

  // Phase 2.3 — Navigation Manager key (CLAUDE.md standing rule 14, §18.6,
  // ADR-0013).  Mirrors the bespoke left-slot "Save" button exactly (same handler,
  // same disabled gate); Refresh (Alt+R) is already bound by the shared
  // <AppToolbar onRefresh>.  Registered unconditionally, before the return.
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'Navigation Manager', order: 1,
      disabled: saving || dirtyCount === 0, run: () => save() },
  ]);

  return (
    <div className="sec-page">
      <div className="sec-head">
        <FormTitle {...titleProps} loadMs={loadMs} loading={loading} auditFormName="Navigation" />
        <div className="sec-tabs">
          <button className={`sec-tab ${tab === 'modules' ? 'active' : ''}`} onClick={() => setTab('modules')}>Modules</button>
          <button className={`sec-tab ${tab === 'forms' ? 'active' : ''}`} onClick={() => setTab('forms')}>Forms</button>
        </div>
      </div>

      <div className="sec-body" style={{ flexDirection: 'column' }}>
        <AppToolbar
          onRefresh={load}
          refreshing={loading}
          left={
            <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving || dirtyCount === 0} title={`Save changes (${formatCombo('mod+s')})`}>
              {saving ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save</>}
            </button>
          }
        />
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder={`Filter ${tab}…`} value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {dirtyCount > 0 && <span className="sec-count"><b style={{ color: '#b5560c' }}>{dirtyCount} unsaved</b></span>}
        </div>

        {err && <div className="sec-error">{err}</div>}

        {tab === 'modules'
          ? <AppDataGrid<ModRowB> rows={shownMods} columns={modColumns} getRowId={r => r.oid} loading={loading} emptyText="No modules." />
          : <AppDataGrid<FormRowB> rows={shownForms} columns={formColumns} getRowId={r => r.oid} loading={loading} emptyText="No forms." />}

        <div className="sec-hint">
          The <b>custom caption</b> overrides the label for everyone (ALPACA + Alpaca Intelligence);
          clearing it reverts to the default. <b>Sort</b> defaults to the current navigation order and
          sets the display order (lower first). Saved changes reorder the live sidebar.
        </div>
      </div>
    </div>
  );
}
